import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import styles from './styles';

const useStyles = createUseStyles(styles, 'Collapse');

const Collapse = memo(
    ({
        animateOpacity = false,
        children,
        className,
        classes: classesProp,
        duration = 250,
        isOpen = false,
        minHeight = 0,
        onAnimationEnd,
        overrides: overridesProp,
        ...props
    }) => {
        const classes = useClasses(useStyles, classesProp);
        const rootRef = useRef(null);
        const contentRef = useRef(null);
        const prevIsOpen = useRef(isOpen);
        const lastOpenHeight = useRef(null);
        const rafId = useRef(null);

        const [height, setHeight] = useState(isOpen ? 'auto' : minHeight);
        const [isAnimating, setIsAnimating] = useState(false);
        const [noTransition, setNoTransition] = useState(false);

        const override = getOverrides(overridesProp, Collapse.overrides);

        // Track height when open (separate effect to avoid dependency issues)
        useEffect(() => {
            if (isOpen && rootRef.current && !isAnimating) {
                lastOpenHeight.current = rootRef.current.offsetHeight;
            }
        });

        useLayoutEffect(() => {
            const root = rootRef.current;
            const content = contentRef.current;
            if (!content || !root) return;

            const wasOpen = prevIsOpen.current;
            prevIsOpen.current = isOpen;

            // Skip animation on initial render
            if (wasOpen === isOpen) return;

            // Cancel any pending animation frame
            if (rafId.current) cancelAnimationFrame(rafId.current);

            if (isOpen) {
                // Opening: animate from minHeight to full height
                setHeight(minHeight);
                setIsAnimating(true);
                rafId.current = requestAnimationFrame(() => {
                    setHeight(content.scrollHeight);
                });
            } else {
                // Closing: use saved height, then animate to minHeight
                const startHeight = lastOpenHeight.current || root.offsetHeight;
                setNoTransition(true);
                setHeight(startHeight);

                rafId.current = requestAnimationFrame(() => {
                    void root.offsetHeight; // Force reflow
                    setNoTransition(false);
                    setIsAnimating(true);
                    rafId.current = requestAnimationFrame(() => {
                        setHeight(minHeight);
                    });
                });
            }

            return () => {
                if (rafId.current) cancelAnimationFrame(rafId.current);
            };
        }, [isOpen, minHeight]);

        const handleTransitionEnd = useCallback(
            (e) => {
                if (e.propertyName !== 'height') return;

                setIsAnimating(false);

                if (isOpen) {
                    setHeight('auto');
                }

                onAnimationEnd?.(isOpen);
            },
            [isOpen, onAnimationEnd],
        );

        const rootClassName = useMemo(
            () =>
                classnames(
                    classes.root,
                    {
                        [classes.open]: isOpen,
                        [classes.animating]: isAnimating,
                        [classes.animateOpacity]: animateOpacity,
                        [classes.noTransition]: noTransition,
                    },
                    className,
                ),
            [classes, isOpen, isAnimating, animateOpacity, noTransition, className],
        );

        const style = useMemo(
            () => ({
                height: height === 'auto' ? 'auto' : `${height}px`,
                transitionDuration: `${duration}ms`,
                ...override.root?.style,
            }),
            [height, duration, override.root?.style],
        );

        return (
            <div
                ref={rootRef}
                className={rootClassName}
                style={style}
                onTransitionEnd={handleTransitionEnd}
                {...override.root}
                {...props}
            >
                <div ref={contentRef} className={classes.content} {...override.content}>
                    {children}
                </div>
            </div>
        );
    },
);

Collapse.displayName = 'Collapse';

Collapse.overrides = ['root', 'content'];

Collapse.propTypes = {
    /** When true, also animates the opacity of the content */
    animateOpacity: PropTypes.bool,
    /** Content to be displayed inside the collapsible area */
    children: PropTypes.node,
    /** Custom CSS class for styling the root element */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    classes: PropTypes.object,
    /** Animation duration in milliseconds */
    duration: PropTypes.number,
    /** Controls whether the content is expanded (true) or collapsed (false) */
    isOpen: PropTypes.bool,
    /** Minimum height when collapsed (in pixels) */
    minHeight: PropTypes.number,
    /** Callback fired when the animation ends */
    onAnimationEnd: PropTypes.func,
    /** Object with custom props overrides for inner elements */
    overrides: PropTypes.shape({
        content: PropTypes.object,
        root: PropTypes.object,
    }),
};

export default Collapse;
